import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { PackageCheck, Plus, Trash2, Upload, Camera, ShieldCheck, Loader2, ClipboardList, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const emptyDraft = { equipment_type: '', brand: '', model: '', serial_number: '', notes: '', inventory_item_id: '' };

// TEMA 3 (audio "el mismo formato para los tres"): Well Construction keeps
// its own Cutting Log — Pump Installer, Filters & Service and Site
// Inspection all reuse this card, only the title/help text change.
const TITLE_BY_CATEGORY = {
  pump_installer: 'Installed Equipment',
  filters_service: 'Installed Filters & Equipment',
  site_inspection: 'Equipment Found On Site',
};
const HELP_BY_CATEGORY = {
  pump_installer: 'Equipment installed at the customer property (pump, tank, etc.) — one entry per item, with photo and serial number for warranty/failure tracking.',
  filters_service: 'Filters and equipment installed/serviced at the customer property — one entry per item, with photo and serial number for warranty/failure tracking.',
  site_inspection: 'Equipment or issues found on the well during the inspection — one entry per item, with photo for the record.',
};

// TEMA D5 (video "Ajustes de informe para Pump Installation"): Alian —
// "Equipment Information... la bomba, el tanque de presión, el pressure
// switch, y después ir poniendo todas esas cosas... con foto y save,
// agregando save" — un tipo por entrada, no un formulario único.
const EQUIPMENT_TYPES = [
  { value: 'pump', label: 'Pump' },
  { value: 'pressure_tank', label: 'Pressure Tank' },
  { value: 'pressure_switch', label: 'Pressure Switch' },
  { value: 'cable', label: 'Cable' },
  { value: 'other', label: 'Other' },
];

/**
 * InstalledEquipmentCard — "el equipo que se le instala al cliente" (Alian):
 * cada entrada guarda tipo/marca/modelo/serial/foto/notas, opcionalmente
 * ligada al catálogo de Inventory (SKU) para trazar garantía/fallas.
 * TEMA D (video "Ajustes de informe para Pump Installation"):
 * - D4: arriba se muestra qué hay que instalar según el ítem del estimado
 *   de origen (job.items), solo lectura, para que el instalador no llegue
 *   sin saber qué bomba le toca poner.
 * - D5: se puede cargar equipo por equipo (tipo, marca/modelo, serial, foto,
 *   notas), guardando entre cada uno con el botón "+" existente.
 * - D6: botón "Complete Job" que exige al menos una foto final antes de
 *   cerrar el trabajo (pueden ser varias).
 */
export default function InstalledEquipmentCard({ job, jobCategory, onSaved }) {
  const [inventory, setInventory] = useState([]);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [photoFile, setPhotoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [completionPhotos, setCompletionPhotos] = useState([]);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    api.get('/inventory').then(res => setInventory(res.data || [])).catch(() => setInventory([]));
  }, []);

  const items = job.installed_equipment || [];
  const toInstall = job.items || [];
  const isCompleted = job.status === 'completed';

  const handleAdd = async () => {
    if (!draft.equipment_type) { toast.error('Select the equipment type'); return; }
    setSaving(true);
    try {
      const invItem = inventory.find(i => i.id === draft.inventory_item_id);
      let photo_file_id = '';
      let photo_url = '';
      if (photoFile) {
        const formData = new FormData();
        formData.append('file', photoFile);
        formData.append('entity_type', 'job');
        formData.append('entity_id', job.id);
        formData.append('doc_type', 'installed_equipment_photo');
        const up = await api.post('/files/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        photo_file_id = up.data.id;
        photo_url = up.data.url;
      }
      const entry = {
        id: crypto.randomUUID(),
        equipment_type: draft.equipment_type,
        equipment_type_label: EQUIPMENT_TYPES.find(t => t.value === draft.equipment_type)?.label || draft.equipment_type,
        brand: draft.brand.trim(),
        model: draft.model.trim(),
        serial_number: draft.serial_number.trim(),
        notes: draft.notes.trim(),
        inventory_item_id: invItem?.id || '',
        inventory_item_name: invItem?.name || '',
        sku: invItem?.sku || '',
        photo_file_id,
        photo_url,
        installed_date: new Date().toISOString().split('T')[0],
      };
      const updated = [...items, entry];
      const res = await api.put(`/jobs/${job.id}`, { installed_equipment: updated });
      onSaved(res.data);
      setDraft(emptyDraft);
      setPhotoFile(null);
      setAdding(false);
      toast.success('Equipment saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error saving');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (idx) => {
    const updated = items.filter((_, i) => i !== idx);
    try {
      const res = await api.put(`/jobs/${job.id}`, { installed_equipment: updated });
      onSaved(res.data);
      toast.success('Deleted');
    } catch {
      toast.error('Error deleting');
    }
  };

  // TEMA D6 — "Complete Job" pide al menos una foto final (pueden ser
  // varias) antes de cerrar el trabajo; el backend YA rechaza status=
  // completed sin ninguna foto (jobs.py), esto solo hace explícito el paso.
  const handleCompleteJob = async () => {
    if (completionPhotos.length === 0) {
      toast.error('Add at least one final photo');
      return;
    }
    setCompleting(true);
    try {
      for (const file of completionPhotos) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('entity_type', 'job');
        formData.append('entity_id', job.id);
        formData.append('doc_type', 'job_completion_photo');
        await api.post('/files/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      const res = await api.put(`/jobs/${job.id}`, { status: 'completed' });
      onSaved(res.data);
      setCompleteOpen(false);
      setCompletionPhotos([]);
      toast.success('Job completed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error completing job');
    } finally {
      setCompleting(false);
    }
  };

  return (
    <Card className="bg-white border-gray-100 shadow-sm" data-testid="installed-equipment-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-sm font-semibold font-['Barlow'] flex items-center gap-2" data-testid="installed-equipment-title">
            <PackageCheck className="w-4 h-4 text-[#3399FF]" /> {TITLE_BY_CATEGORY[jobCategory] || 'Installed Equipment'}
          </CardTitle>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setAdding(!adding)} data-testid="installed-equipment-add-toggle">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add
          </Button>
        </div>
        <p className="text-[11px] text-gray-400">
          {HELP_BY_CATEGORY[jobCategory] || HELP_BY_CATEGORY.pump_installer}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* D4: lo que hay que instalar, según el estimado de origen */}
        {toInstall.length > 0 && (
          <div className="p-2.5 rounded-lg bg-amber-50 border border-amber-200" data-testid="installed-equipment-to-install">
            <p className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold mb-1 flex items-center gap-1.5">
              <ClipboardList className="w-3 h-3" /> To Install (from estimate)
            </p>
            {toInstall.map((item, i) => (
              <p key={i} className="text-xs text-amber-900" data-testid={`to-install-item-${i}`}>
                {item.name || item.description || ''}{item.quantity ? ` — ${item.quantity} ${item.unit || ''}`.replace(/\s+$/, '') : ''}
              </p>
            ))}
          </div>
        )}

        {adding && (
          <div className="p-3 rounded-lg bg-blue-50/50 border border-blue-100 space-y-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-[10px] text-gray-500 uppercase">Equipment Type *</Label>
                <Select value={draft.equipment_type} onValueChange={v => setDraft({ ...draft, equipment_type: v })}>
                  <SelectTrigger data-testid="installed-equipment-type-select" className="h-9 text-xs bg-white"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {EQUIPMENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-gray-500 uppercase">Link to Inventory (optional)</Label>
                <Select value={draft.inventory_item_id} onValueChange={v => setDraft({ ...draft, inventory_item_id: v })}>
                  <SelectTrigger data-testid="installed-equipment-item-select" className="h-9 text-xs bg-white"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    {inventory.map(it => <SelectItem key={it.id} value={it.id}>{it.name}{it.sku ? ` (${it.sku})` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-gray-500 uppercase">Brand</Label>
                <Input className="h-9 text-xs" value={draft.brand} onChange={e => setDraft({ ...draft, brand: e.target.value })} placeholder="e.g. Goulds" data-testid="installed-equipment-brand-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-gray-500 uppercase">Model</Label>
                <Input className="h-9 text-xs" value={draft.model} onChange={e => setDraft({ ...draft, model: e.target.value })} placeholder="Model #" data-testid="installed-equipment-model-input" />
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label className="text-[10px] text-amber-600 uppercase font-semibold">Serial Number</Label>
                <Input className="h-9 text-xs font-mono" value={draft.serial_number} onChange={e => setDraft({ ...draft, serial_number: e.target.value })} placeholder="S/N" data-testid="installed-equipment-serial-input" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-gray-500 uppercase flex items-center gap-1"><Camera className="w-3 h-3" /> Photo</Label>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={e => setPhotoFile(e.target.files?.[0] || null)}
                className="text-xs"
                data-testid="installed-equipment-photo-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-gray-500 uppercase">Notes</Label>
              <Textarea className="text-xs" rows={2} value={draft.notes} onChange={e => setDraft({ ...draft, notes: e.target.value })} placeholder="Optional notes" data-testid="installed-equipment-notes-input" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => { setAdding(false); setDraft(emptyDraft); setPhotoFile(null); }}>Cancel</Button>
              <Button size="sm" className="h-8 text-xs bg-[#003366] hover:bg-[#004080] text-white" onClick={handleAdd} disabled={saving} data-testid="installed-equipment-save-btn">
                {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />} Save
              </Button>
            </div>
          </div>
        )}

        {items.length === 0 ? (
          !adding && <p className="text-xs text-gray-400 italic">No equipment installed yet.</p>
        ) : (
          <div className="space-y-1.5">
            {items.map((eq, i) => (
              <div key={eq.id || i} data-testid={`installed-equipment-item-${i}`} className="flex items-center justify-between py-1.5 px-2.5 rounded-lg bg-slate-50 group">
                <div className="flex items-center gap-2 min-w-0">
                  {eq.photo_url ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${eq.photo_url}`} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-8 h-8 rounded bg-slate-200 flex items-center justify-center flex-shrink-0">
                      <ShieldCheck className="w-4 h-4 text-slate-500" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-800 truncate">
                      {eq.equipment_type_label || eq.inventory_item_name || 'Equipment'}
                      {(eq.brand || eq.model) ? ` — ${[eq.brand, eq.model].filter(Boolean).join(' ')}` : ''}
                      {eq.sku ? ` (${eq.sku})` : ''}
                    </p>
                    <p className="text-[10px] text-gray-400">
                      {eq.serial_number && <span className="font-mono">S/N: {eq.serial_number} · </span>}
                      {eq.installed_date}
                    </p>
                    {eq.notes && <p className="text-[10px] text-gray-400 truncate">{eq.notes}</p>}
                  </div>
                </div>
                <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" onClick={() => handleRemove(i)} data-testid={`installed-equipment-remove-${i}`}>
                  <Trash2 className="w-3 h-3 text-red-400" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* D6: cerrar el trabajo con al menos una foto final */}
        <div className="pt-2 border-t border-gray-100">
          {isCompleted ? (
            <div className="flex items-center gap-2 text-sm text-emerald-700 font-medium" data-testid="pump-job-completed-badge">
              <CheckCircle2 className="w-4 h-4" /> Job completed
            </div>
          ) : (
            <Button
              onClick={() => setCompleteOpen(true)}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="complete-job-btn"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" /> Complete Job
            </Button>
          )}
        </div>
      </CardContent>

      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent className="sm:max-w-[440px]" data-testid="complete-job-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Complete Job
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Add at least one final photo before marking this job as completed. You can add several.
          </p>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            onChange={e => setCompletionPhotos(Array.from(e.target.files || []))}
            className="text-sm"
            data-testid="complete-job-photo-input"
          />
          {completionPhotos.length > 0 && (
            <p className="text-xs text-gray-500" data-testid="complete-job-photo-count">{completionPhotos.length} photo(s) selected</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCompleteOpen(false); setCompletionPhotos([]); }} data-testid="cancel-complete-job-btn">
              Cancel
            </Button>
            <Button
              onClick={handleCompleteJob}
              disabled={completing || completionPhotos.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="confirm-complete-job-btn"
            >
              {completing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Completing...</> : 'Mark as Completed'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
